import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { AppConfig, loadConfig, setConfig } from '@/config/config';

interface ConfigContextType {
  config: AppConfig | null;
  isLoading: boolean;
  error: string | null;
  reloadConfig: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfigState] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfiguration = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const loadedConfig = await loadConfig();
      setConfig(loadedConfig); // Set global config
      setConfigState(loadedConfig);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load configuration';
      setError(errorMessage);
      console.error('Configuration loading failed:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const reloadConfig = async () => {
    await loadConfiguration();
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const value: ConfigContextType = {
    config,
    isLoading,
    error,
    reloadConfig,
  };

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
